import { MediaThumbnail } from "@wags/ui/media-thumbnail.tsx";
import { cn } from "@wags/ui/ui-utils";

type MediaItem = { id: string; url: string; mimeType?: string | null };

export function ProviderPhotoStrip({
    mediaItems,
    alt,
    onPhotoClick,
    layout = "strip",
    className,
}: {
    mediaItems: MediaItem[];
    alt: string;
    onPhotoClick: (index: number) => void;
    layout?: "strip" | "grid";
    className?: string;
}) {
    if (mediaItems.length === 0) return null;

    if (layout === "grid") {
        const visible = mediaItems.slice(0, 6);
        const overflow = mediaItems.length - visible.length;
        return (
            <div className={cn("grid grid-cols-2 gap-3", className)}>
                {visible.map((item, i) => {
                    const showOverflow =
                        i === visible.length - 1 && overflow > 0;
                    return (
                        <button
                            key={item.id}
                            type="button"
                            aria-label={
                                showOverflow
                                    ? `View all ${mediaItems.length} ${alt} photos`
                                    : `View ${alt} photo ${i + 1} full screen`
                            }
                            onClick={() => onPhotoClick(i)}
                            className="relative aspect-4/3 w-full shrink-0 cursor-pointer overflow-hidden rounded-card img-inset"
                        >
                            <MediaThumbnail
                                media={item}
                                alt={`${alt} photo ${i + 1}`}
                                width={400}
                                height={300}
                                className="size-full"
                                variant="interactive"
                            />
                            {showOverflow && (
                                <span className="absolute inset-0 flex items-center justify-center bg-true-black/45 text-item-title text-true-white">
                                    +{overflow}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    }

    const visible = mediaItems.slice(0, 7);
    const overflow = mediaItems.length - visible.length;
    return (
        <div
            className={cn(
                "hidden md:flex md:items-center md:gap-2.5",
                className,
            )}
        >
            {visible.map((item, i) => (
                <button
                    key={item.id}
                    type="button"
                    aria-label={`View ${alt} photo ${i + 1} full screen`}
                    onClick={() => onPhotoClick(i)}
                    className="relative aspect-4/3 h-16 shrink-0 cursor-pointer overflow-hidden rounded-nested img-inset"
                >
                    <MediaThumbnail
                        media={item}
                        alt={`${alt} photo ${i + 1}`}
                        width={200}
                        height={150}
                        className="size-full"
                        variant="interactive"
                    />
                </button>
            ))}
            {overflow > 0 && (
                <button
                    type="button"
                    aria-label={`View all ${mediaItems.length} ${alt} photos`}
                    onClick={() => onPhotoClick(visible.length)}
                    className="grid aspect-4/3 h-16 shrink-0 place-items-center rounded-nested bg-doberman-black/[0.05] text-body-small font-semibold text-doberman-black/55 transition-colors [@media(hover:hover)]:hover:bg-doberman-black/[0.08]"
                >
                    +{overflow}
                </button>
            )}
        </div>
    );
}
